import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Send, MessageCircle, Instagram, ArrowRight, Check } from 'lucide-react';
import { BRAND } from '../data/mock';
import { toast } from 'sonner';

const eventTypes = [
  'Corporate Event',
  'Wedding',
  'Brand Activation',
  'Mall Promotion',
  'Private Party',
  'Other'
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/php-backend/api/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.status === 'success') {
        setIsSubmitted(true);
        toast.success(result.message);

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          eventType: '',
          eventDate: '',
          message: ''
        });

        // Hide success message after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false);
        }, 5000);
      } else {
        toast.error(result.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Could not connect to the server. Please check if the PHP backend is running.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    window.open(
      `https://wa.me/${BRAND.whatsapp}?text=Hi! I'm interested in Inkfly Photo Booth services.`,
      '_blank'
    );
  };

  return (
    <div className="min-h-screen bg-white pt-24">
      {/* Hero */}
      <section className="section-padding pb-8 bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <span className="text-[#00BFFF] text-sm uppercase tracking-widest font-medium">
              Get In Touch
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mt-4 mb-8">
              Let&apos;s Create an{' '}
              <span className="gold-gradient-text">Experience</span>
            </h1>
            <p className="text-gray-600 text-lg md:text-xl leading-relaxed">
              Ready to make your event unforgettable? Get in touch with us and let&apos;s
              discuss how we can create magical moments together.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding pt-8">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-2"
            >
              <h2 className="font-display text-2xl font-bold text-gray-900 mb-8">
                Contact Information
              </h2>

              <div className="space-y-6 mb-10">
                <a
                  href={`tel:${BRAND.phone}`}
                  className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100 hover:border-[#00BFFF]/30 hover:shadow-md transition-all group"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#00BFFF]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#00BFFF]" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Phone</p>
                    <p className="text-gray-900 font-medium group-hover:text-[#00BFFF] transition-colors">
                      {BRAND.phone}
                    </p>
                    <p className="text-gray-900 font-medium group-hover:text-[#00BFFF] transition-colors">
                      {BRAND.phone2}
                    </p>
                  </div>
                </a>

                <a
                  href={`mailto:${BRAND.email}`}
                  className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100 hover:border-[#00BFFF]/30 hover:shadow-md transition-all group"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#00BFFF]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#00BFFF]" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Email</p>
                    <p className="text-gray-900 font-medium group-hover:text-[#00BFFF] transition-colors">
                      {BRAND.email}
                    </p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="w-12 h-12 rounded-xl bg-[#00BFFF]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#00BFFF]" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Location</p>
                    <p className="text-gray-900 font-medium">{BRAND.address}</p>
                    <p className="text-gray-500 text-sm mt-1">Pan-India Service Available</p>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="space-y-4">
                <p className="text-gray-500 text-sm uppercase tracking-wider">Quick Connect</p>

                <button
                  onClick={handleWhatsApp}
                  className="w-full flex items-center justify-center gap-3 py-4 px-6 rounded-xl bg-[#25d366] hover:bg-[#20bd5a] text-white font-medium transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                  Chat on WhatsApp
                </button>

                <a
                  href={BRAND.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-3 py-4 px-6 rounded-xl bg-gradient-to-r from-[#833ab4] via-[#fd1d1d] to-[#fcb045] text-white font-medium transition-opacity hover:opacity-90"
                >
                  <Instagram className="w-5 h-5" />
                  Follow on Instagram
                </a>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="lg:col-span-3"
            >
              <div className="p-8 md:p-10 rounded-2xl bg-gray-50 border border-gray-100">
                <h2 className="font-display text-2xl font-bold text-gray-900 mb-2">
                  Send Us a Message
                </h2>
                <p className="text-gray-600 mb-8">
                  Fill out the form below and we&apos;ll get back to you within 24 hours.
                </p>

                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <div className="w-20 h-20 rounded-full bg-[#00BFFF]/10 flex items-center justify-center mx-auto mb-6">
                      <Check className="w-10 h-10 text-[#00BFFF]" />
                    </div>
                    <h3 className="font-display text-2xl font-bold text-gray-900 mb-2">
                      Message Ready!
                    </h3>
                    <p className="text-gray-600">
                      Your email client should open with the message. Send it to reach us!
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-gray-700 text-sm mb-2" htmlFor="name">
                          Your Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#00BFFF] focus:outline-none focus:ring-1 focus:ring-[#00BFFF] transition-colors"
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 text-sm mb-2" htmlFor="email">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#00BFFF] focus:outline-none focus:ring-1 focus:ring-[#00BFFF] transition-colors"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-gray-700 text-sm mb-2" htmlFor="phone">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#00BFFF] focus:outline-none focus:ring-1 focus:ring-[#00BFFF] transition-colors"
                          placeholder="+91 98765 43210"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 text-sm mb-2" htmlFor="eventType">
                          Event Type *
                        </label>
                        <select
                          id="eventType"
                          name="eventType"
                          value={formData.eventType}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 focus:border-[#00BFFF] focus:outline-none focus:ring-1 focus:ring-[#00BFFF] transition-colors appearance-none cursor-pointer"
                        >
                          <option value="">Select event type</option>
                          {eventTypes.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-700 text-sm mb-2" htmlFor="eventDate">
                        Event Date (Optional)
                      </label>
                      <input
                        type="date"
                        id="eventDate"
                        name="eventDate"
                        value={formData.eventDate}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 focus:border-[#00BFFF] focus:outline-none focus:ring-1 focus:ring-[#00BFFF] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 text-sm mb-2" htmlFor="message">
                        Your Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={4}
                        className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#00BFFF] focus:outline-none focus:ring-1 focus:ring-[#00BFFF] transition-colors resize-none"
                        placeholder="Tell us about your event..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary w-full py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Preparing...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          Send Message <Send className="w-4 h-4" />
                        </span>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Prefer a Quick Call?
            </h2>
            <p className="text-gray-600 mb-8">
              Our team is available to discuss your requirements and provide instant quotes.
            </p>
            <a href={`tel:${BRAND.phone}`} className="btn-primary">
              Call Now <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
